import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { settingsSectionPath } from '@/routes/route-config';
import { Menu } from '@heroui/react/menu';
import { Popover } from '@heroui/react/popover';
import { User, Shield, LogOut, ChevronDown } from 'lucide-react';
import { Avatar } from '@/components/ds';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/utils/cn';

/**
 * Account menu, on HeroUI's Menu/Popover.
 *
 * Replaces the hand-rolled `UserMenuDropdown` (127 LOC: a `useState`,
 * a `useRef`, and a document-level mousedown listener for click-away)
 * which had no roving focus, no `role="menu"`, no Escape handling and
 * no focus restoration.
 */
export interface ShellUser {
  name?: string;
  email?: string;
  specialty?: string;
  photoUrl?: string | null;
}

export function UserMenu({ user }: { user: ShellUser }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={t('nav.accountMenu', 'Account menu')}
        className={cn(
          'flex items-center gap-2.5 rounded-md py-1 pe-1 ps-1.5',
          'transition-colors duration-(--shell-motion-fast)',
          'hover:bg-surface-2',
          'outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        )}
      >
        <Avatar name={user.name} src={user.photoUrl} size="sm" />
        <span className="hidden text-start leading-tight sm:block">
          <span className="block max-w-36 truncate text-small font-semibold text-secondary-800">
            {user.name || t('nav.user', 'User')}
          </span>
          {user.specialty && (
            <span className="block truncate text-micro text-secondary-400">
              {user.specialty}
            </span>
          )}
        </span>
        <ChevronDown
          aria-hidden
          className="hidden size-4 shrink-0 text-secondary-400 sm:block"
        />
      </Popover.Trigger>

      <Popover.Content placement="bottom end" className="w-60 p-1">
        <div className="border-b border-border-subtle px-3 py-2.5">
          <p className="truncate text-small font-semibold text-secondary-800">
            {user.name}
          </p>
          <p className="truncate text-micro text-secondary-500">{user.email}</p>
        </div>

        <Menu
          aria-label={t('nav.accountMenu', 'Account menu')}
          className="pt-1"
        >
          <Menu.Item
            onAction={() => navigate(settingsSectionPath('profile'))}
            className="flex items-center gap-2.5 text-small"
          >
            <User aria-hidden className="size-4" />
            {t('nav.profile')}
          </Menu.Item>
          <Menu.Item
            onAction={() =>
              navigate(`${settingsSectionPath('profile')}#security`)
            }
            className="flex items-center gap-2.5 text-small"
          >
            <Shield aria-hidden className="size-4" />
            {t('nav.security')}
          </Menu.Item>
          <Menu.Item
            onAction={() => void logout()}
            className="mt-1 flex items-center gap-2.5 border-t border-border-subtle pt-2 text-small text-state-danger-text"
          >
            <LogOut aria-hidden className="size-4" />
            {t('nav.logout')}
          </Menu.Item>
        </Menu>
      </Popover.Content>
    </Popover.Root>
  );
}
